import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { userService } from '../services/userService';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import UserLanguageSwitcher from '../components/common/UserLanguageSwitcher';
import './MajesticCommon.css';
import '../components/dashboard/Dashboard.css';

const CryptoWalletListPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [wallets, setWallets] = useState([]);
    const [loading, setLoading] = useState(true);

    const [visibleItems, setVisibleItems] = useState({});
    const [selectedQR, setSelectedQR] = useState(null);

    const toggleVisibility = (id) => {
        setVisibleItems(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    useEffect(() => {
        fetchWallets();
    }, []);

    const fetchWallets = async () => {
        try {
            const data = await userService.getCryptoWallets();
            if (data.success) {
                setWallets(data.wallets);
            }
        } catch (error) {
            console.error('Failed to fetch wallets:', error);
            toast.error(t('msg_failed_load_wallets', 'Failed to load crypto wallets'));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm(t('confirm_delete_wallet', 'Are you sure you want to delete this wallet?'))) {
            try {
                const data = await userService.deleteCryptoWallet(id);
                if (data.success) {
                    toast.success(t('msg_wallet_deleted', 'Wallet deleted'));
                    fetchWallets();
                }
            } catch (error) {
                console.error('Failed to delete wallet:', error);
                toast.error(error.message || t('msg_failed_delete_wallet', 'Failed to delete wallet'));
            }
        }
    };

    return (
        <div className="majestic-page-wrapper">
            {/* Majestic Header */}
            <div className="majestic-header d-flex align-items-center px-3 py-3 justify-content-between">
                <div style={{ width: '40px' }}>
                    <i
                        className="bi bi-chevron-left text-white fs-4 cursor-pointer"
                        onClick={() => navigate('/wallet')}
                    ></i>
                </div>
                <h1 className="majestic-header-title flex-grow-1 text-center">
                    {t('crypto_wallet_list', 'My Crypto Wallet')}
                </h1>
                <div style={{ width: '40px' }} className="d-flex justify-content-end">
                    <UserLanguageSwitcher />
                </div>
            </div>

            <div className="majestic-container animate-slide-up">
                {/* User Info Header in List */}
                <div className="d-flex align-items-center justify-content-between mb-4 p-3 rounded-4" style={{ background: 'rgba(252, 213, 53, 0.05)', border: '1px solid rgba(252, 213, 53, 0.1)' }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="rounded-circle bg-warning d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                            <i className="bi bi-shield-check-fill text-dark fs-5"></i>
                        </div>
                        <div>
                            <div className="text-secondary small fw-bold text-uppercase" style={{ fontSize: '10px', letterSpacing: '1px' }}>{t('secure_vault', 'SECURE VAULT')}</div>
                            <div className="text-white fw-bold">{t('encrypted_addresses', 'Encrypted Addresses')}</div>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="wallet-list">
                    {loading ? (
                        <div className="text-center p-5 text-secondary">{t('loading', 'Loading...')}</div>
                    ) : wallets.length === 0 ? (
                        <div className="text-center p-5 text-secondary">
                            <i className="bi bi-wallet2 fs-1 opacity-25 mb-3 d-block"></i>
                            {t('no_data', 'No wallets secured yet')}
                        </div>
                    ) : (
                        wallets.map((wallet) => (
                            <div key={wallet.id} className="majestic-card">
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="rounded-3 d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px', background: 'rgba(252, 213, 53, 0.1)' }}>
                                            <i className="bi bi-currency-bitcoin text-warning"></i>
                                        </div>
                                        <div>
                                            <div className="text-secondary extra-small fw-bold text-uppercase" style={{ fontSize: '9px', letterSpacing: '1px' }}>{t('network', 'Network')}</div>
                                            <div className="text-white fw-bold">{wallet.network}</div>
                                        </div>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <div
                                            className="majestic-icon-btn text-warning"
                                            onClick={() => toggleVisibility(wallet.id)}
                                            style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                                            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                                            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                        >
                                            <i className={`bi ${visibleItems[wallet.id] ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                                        </div>
                                        <div
                                            className="majestic-delete-btn text-danger bg-danger bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                            style={{ width: '32px', height: '32px', cursor: 'pointer', transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)' }}
                                            onClick={() => handleDelete(wallet.id)}
                                            onMouseOver={(e) => {
                                                e.currentTarget.style.transform = 'scale(1.1) rotate(5deg)';
                                                e.currentTarget.style.background = 'rgba(220, 53, 69, 0.2)';
                                            }}
                                            onMouseOut={(e) => {
                                                e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                                                e.currentTarget.style.background = 'rgba(220, 53, 69, 0.1)';
                                            }}
                                        >
                                            <i className="bi bi-trash3"></i>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-3 rounded-3 position-relative" style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.03)' }}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="flex-grow-1 pe-3">
                                            <div className="text-secondary extra-small fw-bold text-uppercase mb-1" style={{ fontSize: '9px', letterSpacing: '1px' }}>{t('wallet_address', 'Wallet Address')}</div>
                                            <div className="text-white fw-bold break-all" style={{ fontSize: '14px', letterSpacing: '0.5px' }}>
                                                {visibleItems[wallet.id] ? wallet.walletAddress : '********************************'}
                                            </div>
                                            <div className="mt-2 text-success border-success border bg-success bg-opacity-10 px-2 py-1 rounded d-inline-block" style={{ fontSize: '10px' }}>
                                                <i className="bi bi-check-circle-fill me-1"></i> {t('active', 'ACTIVE')}
                                            </div>
                                        </div>
                                        {/* QR Code */}
                                        {wallet.qrCodeImage && (
                                            <div className="bg-white p-1 rounded-2" style={{ flexShrink: 0 }}>
                                                <div
                                                    style={{
                                                        position: 'relative',
                                                        width: '48px',
                                                        height: '48px',
                                                        cursor: visibleItems[wallet.id] ? 'pointer' : 'default'
                                                    }}
                                                    onClick={() => {
                                                        if (visibleItems[wallet.id]) {
                                                            setSelectedQR(wallet.qrCodeImage);
                                                        }
                                                    }}
                                                >
                                                    <img
                                                        src={wallet.qrCodeImage}
                                                        alt="QR"
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'contain',
                                                            filter: visibleItems[wallet.id] ? 'none' : 'blur(5px)',
                                                            transition: 'filter 0.3s ease'
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Add Button */}
                <div className="mt-4">
                    <button
                        className="btn majestic-btn-primary w-100 py-3"
                        onClick={() => navigate('/my-crypto-wallet/add')}
                    >
                        <i className="bi bi-plus-lg me-2"></i>
                        {t('add_wallet_address', 'Add Wallet Address')}
                    </button>
                </div>
            </div>

            {/* QR Large View Modal - Moved outside majestic-container to allow perfect viewport centering */}
            {selectedQR && (
                <div
                    className="qr-modal-overlay"
                    onClick={() => setSelectedQR(null)}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.85)',
                        backdropFilter: 'blur(8px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        animation: 'fadeIn 0.3s ease'
                    }}
                >
                    <div
                        className="qr-modal-content"
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: 'linear-gradient(145deg, #1a1a1a, #0d0d0d)',
                            padding: '25px',
                            borderRadius: '24px',
                            border: '1px solid rgba(212, 175, 55, 0.3)',
                            boxShadow: '0 0 50px rgba(212, 175, 55, 0.2)',
                            maxWidth: '90%',
                            width: '320px',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            textAlign: 'center',
                            position: 'relative'
                        }}
                    >
                        <div
                            style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: '24px',
                                zIndex: 10
                            }}
                            onClick={() => setSelectedQR(null)}
                        >
                            <i className="bi bi-x-circle-fill opacity-75"></i>
                        </div>

                        <div className="mb-3">
                            <i className="bi bi-qr-code text-warning fs-3"></i>
                            <h5 className="text-white mt-2 fw-bold">{t('wallet_qr_code', 'Wallet Address QR Code')}</h5>
                        </div>

                        <div
                            style={{
                                background: '#fff',
                                padding: '15px',
                                borderRadius: '16px',
                                boxShadow: '0 5px 15px rgba(0,0,0,0.5)'
                            }}
                        >
                            <img
                                src={selectedQR}
                                alt="QR Large"
                                style={{
                                    width: '100%',
                                    maxHeight: '50vh',
                                    height: 'auto',
                                    display: 'block',
                                    borderRadius: '8px',
                                    objectFit: 'contain'
                                }}
                            />
                        </div>

                        <button
                            className="btn btn-warning w-100 mt-4 py-2 fw-bold"
                            style={{ borderRadius: '12px', background: '#D4AF37', border: 'none', color: '#000' }}
                            onClick={() => setSelectedQR(null)}
                        >
                            {t('close', 'Close')}
                        </button>
                    </div>
                </div>
            )}

            <style>
                {`
                @keyframes fadeIn {
                    from { opacity: 0; transform: scale(0.95); }
                    to { opacity: 1; transform: scale(1); }
                }
                .qr-modal-content {
                    animation: slideUpModal 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                }
                @keyframes slideUpModal {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                `}
            </style>
        </div>
    );
};

export default CryptoWalletListPage;
